import { Box, Center, IconButton, Slide, Text, useBreakpointValue } from '@chakra-ui/react'
import { faClose } from '@fortawesome/pro-solid-svg-icons'
import { orderBy } from 'lodash-es'
import { useEffect } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import hasMatchingAncestor from '@/utils/hasMatchingAncestor'

import isLiveHelpRequestActive from '@/client/services/liveHelp/isLiveHelpRequestActive'

import { POST_CARD_CLASS_NAME } from '@/components/Community/Post/constants'
import PostFull from '@/components/Community/Post/PostFull'
import PlatformHeaderBase from '@/components/Layout/PlatformHeaderBase'
import Spinner from '@/components/Spinner'
import Icon from '@/components/UI/Icon/Icon'
import { MENTION_SUGGESTION_LIST_CLASS_NAME } from '@/components/UI/RichTextEditor/constants'
import useRealtimeLiveHelpRequests from '@/components/Worlds/hooks/post/useRealtimeLiveHelpRequests'
import useRealtimePost from '@/components/Worlds/hooks/post/useRealtimePost'
import type { LiveHelpRequestWithNullableSessionDetails } from '@/components/Worlds/types'
import {
  NAVBAR_MOBILE_HEIGHT,
  NAVBAR_WIDTH,
  PAGE_HEADER_HEIGHT,
} from '@/components/Worlds/UI/constants'

const POST_DRAWER_CLASS_NAME = 'post-drawer'

export default function ExpandedPostDrawer({
  postId,
  onClose,
}: {
  postId: number
  onClose: () => void
}): JSX.Element | null {
  const { post, currentUser, loading, postEvents, postFeedbacks } = useRealtimePost(postId)

  const { liveHelpRequests, loading: loadingLiveHelpRequests } = useRealtimeLiveHelpRequests(
    post?.id
  )

  const mostRecentLiveHelpRequest: LiveHelpRequestWithNullableSessionDetails | null =
    orderBy(liveHelpRequests, request => -request.createdAt.toMillis())[0] ?? null

  const shouldBlockClose =
    loadingLiveHelpRequests ||
    (mostRecentLiveHelpRequest != null &&
      isLiveHelpRequestActive(mostRecentLiveHelpRequest) &&
      mostRecentLiveHelpRequest.userId === currentUser?.id)

  const drawerWidth = useBreakpointValue({
    base: '100vw',
    sm: `calc(100vw - ${NAVBAR_WIDTH})`,
    lg: '600px',
  })

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (shouldBlockClose) {
        return
      }
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.body.addEventListener('keydown', handleKeyPress)

    return () => {
      document.body.removeEventListener('keydown', handleKeyPress)
    }
  }, [onClose, shouldBlockClose])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (shouldBlockClose) {
        return
      }

      // eslint-disable-next-line
      const element = e.target as HTMLElement
      if (
        hasMatchingAncestor(
          element,
          el =>
            el.tagName === 'BUTTON' ||
            el.tagName === 'A' ||
            el.classList.contains(POST_CARD_CLASS_NAME) ||
            el.classList.contains(POST_DRAWER_CLASS_NAME) ||
            el.classList.contains(MENTION_SUGGESTION_LIST_CLASS_NAME)
        )
      ) {
        return
      }

      onClose()
    }

    document.body.addEventListener('click', handleClick)

    return () => {
      document.body.removeEventListener('click', handleClick)
    }
  }, [onClose, shouldBlockClose])

  if (post?.id == null) {
    return null
  }

  return (
    <Slide
      in={post != null}
      direction="right"
      style={{
        zIndex: 4,
        width: drawerWidth,
        top: 0,
        height: '100vh',
      }}
      className={POST_DRAWER_CLASS_NAME}
      transition={{ enter: { duration: 0 } }}
    >
      <Box
        position="absolute"
        width="100%"
        bg="bg"
        height="100vh"
        boxShadow={{ base: 'none', lg: `0 0 6px 1px ${chakraTokenToVar('border.subtle')}` }}
      >
        <PlatformHeaderBase
          bgColor="bg"
          borderBottom={`1px solid ${chakraTokenToVar('border')}`}
          leftContent={<Text>{`${post?.user.displayName}'s Post`}</Text>}
          {...(!shouldBlockClose
            ? {
                rightContent: (
                  <Box width="36px" textAlign="right" fontSize="20px">
                    <IconButton
                      variant="icon"
                      cursor="pointer"
                      borderRadius="50%"
                      icon={<Icon size="md" faIcon={faClose} />}
                      aria-label="Close post"
                      onClick={onClose}
                    />
                  </Box>
                ),
              }
            : {})}
        />
        <Box
          height={{
            base: `calc(100vh - ${NAVBAR_MOBILE_HEIGHT} - ${PAGE_HEADER_HEIGHT})`,
            sm: `calc(100vh - ${PAGE_HEADER_HEIGHT})`,
          }}
          overflowY="auto"
        >
          {post == null || postFeedbacks == null || loading || loadingLiveHelpRequests ? (
            <Center mt="32px">
              <Spinner size="md" />
            </Center>
          ) : (
            <PostFull
              post={post}
              postEvents={postEvents}
              postFeedbacks={postFeedbacks}
              postIsFullView={false}
              // included to render current user when they make a comment
              currentUser={currentUser}
            />
          )}
        </Box>
      </Box>
    </Slide>
  )
}
